import { useMemo, useState } from 'react'

import { PALLET_STATUS_LABEL, PALLET_STATUS_ORDER } from '../types'
import type {
  CodeOrderDetail,
  CodeOrderItem,
  CodeOrderPallet,
  CodeOrderTruck,
  PalletStatus,
} from '../types'

/**
 * Дерево поставки: грузовик → товар → паллеты.
 *
 * Один и тот же вид у нас и в кабинете — спорить о том, что написано в файле,
 * должно быть не о чем. Разница только в правах.
 *
 * Выделение считается по паллетам, даже когда галочку ставят на грузовике:
 * все массовые действия в итоге делаются над паллетами, и вести счёт
 * по-разному для разных уровней значило бы однажды разойтись.
 *
 * Грузовики свёрнуты. В поставке их двадцать, в каждом до двадцати шести
 * паллет: развернуть всё разом — полтысячи строк, сквозь которые ничего не
 * найти.
 */
export interface TreeActions {
  /** Завести поставку файлом — со стартового экрана пустого дерева */
  onSource?: (file: File) => void
  /** Собрать поставку руками, без файла */
  onBuild?: () => void
  /** Собрать файл заказа в Честный Знак на этот грузовик */
  onCzOrder?: (truckId: number, name: string) => void
  /** Загрузить коды из СУЗ на один товар */
  onCodes?: (itemId: number, files: File[]) => void
  /** Разрезать коды товаров по паллетам */
  onSplit?: (itemIds: number[]) => void
  /** Приложить декларацию к грузовику или к паллете */
  onGtd?: (where: { truckId?: number; palletId?: number }) => void
  busy?: boolean
}

export function allPallets(order: CodeOrderDetail): CodeOrderPallet[] {
  return order.trucks.flatMap((t) => t.items.flatMap((i) => i.pallets))
}

export default function CodeOrderTree({
  order,
  onDownload,
  actions,
  selected,
  onSelect,
}: {
  order: CodeOrderDetail
  onDownload?: (fileId: number, name: string) => void
  actions?: TreeActions
  /** Выделенные паллеты. Не задано — галочек нет вовсе. */
  selected?: Set<number>
  onSelect?: (ids: number[], on: boolean) => void
}) {
  const [открыт, setОткрыт] = useState<number[]>(() =>
    order.trucks.length ? [order.trucks[0].id] : [],
  )

  if (order.trucks.length === 0) {
    // Пустое дерево — это не сообщение об ошибке, а начало работы: здесь
    // человек и выбирает, как заводить поставку. Раньше тут было «дерева пока
    // нет», и дальше приходилось искать кнопку глазами по всему окну.
    return (
      <div className="cot-start">
        <div className="cot-start-title">Поставки ещё нет</div>
        <p className="hint">Заведите её одним из двух способов.</p>
        <div className="cot-start-ways">
          {actions?.onSource ? (
            <label className="cot-way" style={{ cursor: 'pointer' }}>
              <span className="cot-way-ico">📊</span>
              <span className="cot-way-t">Загрузить файл заказа</span>
              <span className="cot-way-h">
                Та же таблица, по которой вы работаете сами: грузовики, товары,
                номера паллет
              </span>
              <input
                type="file"
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                disabled={actions.busy}
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) actions.onSource!(f)
                }}
              />
            </label>
          ) : null}
          {actions?.onBuild ? (
            <button className="cot-way" onClick={actions.onBuild}>
              <span className="cot-way-ico">✍</span>
              <span className="cot-way-t">Собрать без файла</span>
              <span className="cot-way-h">
                Если таблицы нет: грузовик, товары из справочника, количество.
                Номера паллет посчитает программа
              </span>
            </button>
          ) : null}
        </div>
        {!actions && (
          <p className="empty" style={{ padding: 0 }}>
            Файл заказа ещё не разобран — дерева пока нет.
          </p>
        )}
      </div>
    )
  }

  const переключить = (id: number) =>
    setОткрыт((было) => (было.includes(id) ? было.filter((x) => x !== id) : [...было, id]))

  return (
    <div className="cot">
      <div className="cot-tools">
        <button className="link-btn" onClick={() => setОткрыт(order.trucks.map((t) => t.id))}>
          развернуть всё
        </button>
        <button className="link-btn" onClick={() => setОткрыт([])}>
          свернуть всё
        </button>
      </div>

      {order.trucks.map((t) => (
        <Truck
          key={t.id}
          truck={t}
          open={открыт.includes(t.id)}
          onToggle={() => переключить(t.id)}
          onDownload={onDownload}
          actions={actions}
          selected={selected}
          onSelect={onSelect}
        />
      ))}
    </div>
  )
}

/** Галочка с третьим состоянием: часть вложенного выделена. */
function Check({
  state,
  onChange,
}: {
  state: 'on' | 'off' | 'some'
  onChange: (on: boolean) => void
}) {
  return (
    <input
      type="checkbox"
      className="cot-check"
      checked={state === 'on'}
      ref={(el) => {
        if (el) el.indeterminate = state === 'some'
      }}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => onChange(e.target.checked)}
    />
  )
}

function состояние(ids: number[], selected?: Set<number>): 'on' | 'off' | 'some' {
  if (!selected || ids.length === 0) return 'off'
  const сколько = ids.filter((id) => selected.has(id)).length
  if (сколько === 0) return 'off'
  return сколько === ids.length ? 'on' : 'some'
}

function Truck({
  truck,
  open,
  onToggle,
  onDownload,
  actions,
  selected,
  onSelect,
}: {
  truck: CodeOrderTruck
  open: boolean
  onToggle: () => void
  onDownload?: (fileId: number, name: string) => void
  actions?: TreeActions
  selected?: Set<number>
  onSelect?: (ids: number[], on: boolean) => void
}) {
  const паллеты = useMemo(
    () => truck.items.flatMap((i) => i.pallets.map((p) => p.id)),
    [truck],
  )
  const единиц = truck.items.reduce(
    (s, i) => s + i.pallets.reduce((n, p) => n + p.units, 0),
    0,
  )
  const гтд = truck.files.filter((f) => f.kind === 'gtd')
  const неузнанные = truck.items.filter((i) => !i.gtin).length
  // Сколько позиций уже с кодами и сколько разложено. Без этого о грузовике
  // ничего не узнать, не раскрыв его, — а их двадцать.
  const с_кодами = truck.items.filter((i) =>
    i.files.some((f) => f.kind === 'codes'),
  ).length
  const разложено = truck.items.filter((i) =>
    i.pallets.some((p) => p.files.some((f) => f.kind === 'pallet_codes')),
  ).length
  const не_хватает = truck.items.reduce((s, i) => s + i.codes_short, 0)

  return (
    <div className={`cot-truck${open ? ' open' : ''}`}>
      <div className="cot-head">
        {onSelect && (
          <Check
            state={состояние(паллеты, selected)}
            onChange={(on) => onSelect(паллеты, on)}
          />
        )}
        <button className="cot-head-main" onClick={onToggle}>
          <span className="cot-caret">{open ? '▾' : '▸'}</span>
          <span className="cot-no">Грузовик {truck.truck_no || '—'}</span>
          <span className="cot-order">{truck.order_no || 'без номера'}</span>
          <span className="cot-sub">
            {truck.items.length} поз. · {паллеты.length} паллет ·{' '}
            {единиц.toLocaleString('ru')} шт
          </span>
          {неузнанные > 0 && <span className="cot-nogtin">не узнано: {неузнанные}</span>}
          {с_кодами > 0 && (
            <span
              className={`cot-mark${
                не_хватает > 0 ? ' warn' : с_кодами === truck.items.length ? ' ok' : ''
              }`}
            >
              коды {с_кодами}/{truck.items.length}
              {не_хватает > 0 && ` · не хватает ${не_хватает.toLocaleString('ru')}`}
            </span>
          )}
          {разложено > 0 && (
            <span className={`cot-mark${разложено === truck.items.length ? ' ok' : ''}`}>
              разложено {разложено}/{truck.items.length}
            </span>
          )}
          {гтд.length > 0 && <span className="cot-gtd-mark">ГТД</span>}
          {truck.address && <span className="cot-addr">{truck.address}</span>}
        </button>

        {actions && (
          <span className="cot-head-tools">
            {actions.onCzOrder && (
              <button
                className="btn tiny"
                disabled={actions.busy}
                title="Файл заказа в Честный Знак на этот грузовик"
                onClick={() =>
                  actions.onCzOrder!(truck.id, `${truck.truck_no}_${truck.order_no}.xlsx`)
                }
              >
                заказ ЧЗ
              </button>
            )}
            {actions.onGtd && (
              <button
                className="btn tiny"
                disabled={actions.busy}
                title="Приложить декларацию к грузовику"
                onClick={() => actions.onGtd!({ truckId: truck.id })}
              >
                + ГТД
              </button>
            )}
          </span>
        )}
      </div>

      {open && (
        <div className="cot-items">
          {гтд.map((f) => (
            <button
              key={f.id}
              className="tk-file"
              onClick={() => onDownload?.(f.id, f.original_name)}
            >
              📑 {f.gtd_number ? `ГТД ${f.gtd_number}` : f.original_name}
              {f.gtd_date && <span className="cell-sub">от {f.gtd_date}</span>}
            </button>
          ))}
          {truck.items.map((i) => (
            <Item
              key={i.id}
              item={i}
              onDownload={onDownload}
              actions={actions}
              selected={selected}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function Item({
  item,
  onDownload,
  actions,
  selected,
  onSelect,
}: {
  item: CodeOrderItem
  onDownload?: (fileId: number, name: string) => void
  actions?: TreeActions
  selected?: Set<number>
  onSelect?: (ids: number[], on: boolean) => void
}) {
  const [раскрыт, setРаскрыт] = useState(false)
  const коды = item.files.find((f) => f.kind === 'codes')
  const ids = item.pallets.map((p) => p.id)

  // Состояние позиции — по самой отстающей паллете: пока хоть одна не дошла,
  // позиция не дошла. Иначе «агрегировано» значило бы «кое-что».
  const отстающее = item.pallets.reduce<PalletStatus | null>(
    (худшее, p) =>
      !худшее || PALLET_STATUS_ORDER.indexOf(p.status) < PALLET_STATUS_ORDER.indexOf(худшее)
        ? p.status
        : худшее,
    null,
  )

  return (
    <div className={`cot-item${item.notes.length ? ' warn' : ''}`}>
      <div className="cot-item-head">
        {onSelect && (
          <Check state={состояние(ids, selected)} onChange={(on) => onSelect(ids, on)} />
        )}
        <span className="cot-product">{item.product}</span>
        {item.gtin ? (
          <span className="cot-gtin">{item.gtin}</span>
        ) : (
          <span className="cot-nogtin">нет в справочнике</span>
        )}
        {коды && item.codes_short === 0 && (
          <span className="cot-mark ok">✓ коды {коды.rows?.toLocaleString('ru')}</span>
        )}
        {отстающее && (
          <span className={`pal-chip pal-${отстающее}`}>
            {PALLET_STATUS_LABEL[отстающее]}
          </span>
        )}
      </div>

      <div className="cot-line">
        <span>
          {item.per_pack ?? '—'} шт × {item.packs_per_pallet ?? '—'} уп ={' '}
          <b>{item.units_per_pallet?.toLocaleString('ru') ?? '—'}</b> на паллете
        </span>
        <span>
          паллет <b>{item.pallets.length}</b>
        </span>
        <span>
          всего <b>{item.units_total?.toLocaleString('ru') ?? '—'}</b> шт
        </span>
        {коды && (
          <button
            className="link-btn"
            title={коды.original_name}
            onClick={() => onDownload?.(коды.id, коды.original_name)}
          >
            файл кодов
          </button>
        )}
        {/* Недостача — не повод прятать то, что уже пришло, но и молчать о
            ней нельзя: по неполной позиции паллеты не нарежешь. */}
        {item.codes_short > 0 && (
          <span className="cot-short">
            не хватает {item.codes_short.toLocaleString('ru')} — дозакажите
          </span>
        )}
        <button className="link-btn" onClick={() => setРаскрыт(!раскрыт)}>
          {раскрыт ? 'скрыть паллеты' : `паллеты (${item.pallets.length})`}
        </button>
      </div>

      {/* Расхождения — прямо у позиции: именно их человек и должен увидеть
          глазами, прежде чем подтвердить разбор. */}
      {item.notes.map((n, i) => (
        <div key={i} className="cot-note">
          ⚠ {n}
        </div>
      ))}

      {actions && (actions.onCodes || actions.onSplit) && (
        <div className="cot-item-tools">
          {/* Когда коды на месте, кнопка загрузки уходит: она сделала своё
              дело, и держать её рядом — значит каждый раз спрашивать себя,
              загружены коды или нет. Остаётся тихая ссылка «заменить». */}
          {actions.onCodes && (
            <label
              className={коды && item.codes_short === 0 ? 'link-btn' : 'btn tiny'}
              style={{ cursor: 'pointer' }}
            >
              {!коды
                ? 'загрузить коды'
                : item.codes_short > 0
                  ? 'догрузить коды'
                  : 'заменить'}
              <input
                type="file"
                accept=".csv,.txt"
                multiple
                style={{ display: 'none' }}
                disabled={actions.busy}
                onChange={(e) => {
                  const список = Array.from(e.target.files ?? [])
                  if (список.length) actions.onCodes!(item.id, список)
                }}
              />
            </label>
          )}
          {actions.onSplit && коды && (
            <button
              className="btn tiny"
              disabled={actions.busy || item.codes_short > 0}
              title={
                item.codes_short > 0
                  ? 'Пока кодов не хватает, резать нечего'
                  : undefined
              }
              onClick={() => actions.onSplit!([item.id])}
            >
              разбить по паллетам
            </button>
          )}
        </div>
      )}

      {раскрыт && (
        <div className="cot-pallets">
          {item.pallets.map((p) => (
            <span key={p.id} className={`cot-pallet pal-${p.status}`} title={p.status_title}>
              {onSelect && (
                <Check
                  state={selected?.has(p.id) ? 'on' : 'off'}
                  onChange={(on) => onSelect([p.id], on)}
                />
              )}
              <b>{p.number}</b>
              {p.sscc && <span className="cot-sscc">{p.sscc}</span>}
              {p.files.map((f) => (
                <button
                  key={f.id}
                  className="cot-pallet-file"
                  title={f.original_name}
                  onClick={() => onDownload?.(f.id, f.original_name)}
                >
                  {f.kind === 'pallet_xml' ? 'xml' : f.kind === 'gtd' ? 'гтд' : 'csv'}
                </button>
              ))}
              {actions?.onGtd && (
                <button
                  className="cot-pallet-file"
                  title="ГТД на эту паллету"
                  disabled={actions.busy}
                  onClick={() => actions.onGtd!({ palletId: p.id })}
                >
                  +гтд
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
